import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useContract } from '../hooks/useContract';
import { userAPI } from '../services/api';
import './Profile.css';

interface UserProfile {
  walletAddress: string;
  name: string;
  email: string;
  bio: string;
  profileImage: string;
}

export function Profile() {
  const { address: walletAddress } = useParams<{ address?: string }>();
  const { address: connectedAddress, isConnected } = useAccount();
  const { getUserPages, loading } = useContract();
  const [pageIds, setPageIds] = useState<number[]>([]);
  const [totalUnlocks] = useState(0);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const navigate = useNavigate();

  // Determine which address to show (param or connected wallet)
  const displayAddress = walletAddress || connectedAddress;
  const isOwnProfile = !walletAddress || (connectedAddress && walletAddress.toLowerCase() === connectedAddress.toLowerCase());

  useEffect(() => {
    // If no param and not connected, redirect to home
    if (!walletAddress && (!isConnected || !connectedAddress)) {
      navigate('/');
      return;
    }

    loadProfile();
    loadUserPages();
  }, [displayAddress, isConnected, connectedAddress]);

  const loadProfile = async () => {
    if (!displayAddress) return;

    setLoadingProfile(true);
    try {
      const response = await userAPI.getProfile(displayAddress);
      setUserProfile(response.user);
    } catch (error: any) {
      console.error('Error loading profile:', error);
      // If viewing own profile and it doesn't exist, redirect to setup
      if (isOwnProfile && error.response?.status === 404) {
        navigate('/profile-setup');
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const loadUserPages = async () => {
    if (!displayAddress) return;
    const ids = await getUserPages(displayAddress);
    setPageIds(ids);
    // TODO: Calculate total unlocks from all pages
  };

  if (loadingProfile) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        {userProfile?.profileImage ? (
          <img src={userProfile.profileImage} alt="Profile" className="profile-avatar-img" />
        ) : (
          <div className="profile-avatar">👤</div>
        )}
        <h1 className="profile-title">{userProfile?.name || 'Anonymous User'}</h1>
        <p className="profile-address">
          {displayAddress?.substring(0, 10)}...{displayAddress?.substring(38)}
        </p>
        {userProfile?.bio && (
          <p className="profile-bio">{userProfile.bio}</p>
        )}
        {!isOwnProfile && (
          <button
            className="btn-view-book"
            onClick={() => navigate(`/book/${displayAddress}`)}
          >
            📖 View Their Book
          </button>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{pageIds.length}</div>
          <div className="stat-label">Secrets Created</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalUnlocks}</div>
          <div className="stat-label">Total Unlocks</div>
        </div>
      </div>

      <div className="actions-section">
        <button className="action-button primary" onClick={() => navigate('/book')}>
          📖 Open My ₿ook
        </button>
        <button className="action-button secondary" onClick={() => navigate('/create')}>
          ✍️ Create New Page
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your pages...</p>
        </div>
      ) : pageIds.length > 0 ? (
        <div className="pages-section">
          <h2 className="section-title">Your Secret Pages</h2>
          <div className="pages-list">
            {pageIds.map((id) => (
              <div key={id} className="page-item" onClick={() => navigate('/book')}>
                <div className="page-icon">📄</div>
                <div className="page-info">
                  <div className="page-id">Page #{id}</div>
                  <div className="page-status">Owned by you</div>
                </div>
                <div className="page-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📖</div>
          <h3>No Pages Yet</h3>
          <p>Create your first secret page to get started!</p>
          <button className="empty-cta" onClick={() => navigate('/create')}>
            Create First Page
          </button>
        </div>
      )}
    </div>
  );
}
